/**
 * Fast location utilities - Optimized location handling for modern URLs
 * Includes city detection for address validation and user address selection
 */

import { getCityByPointFn } from '@/api/ahafood/get-city-by-point-fn';

export interface CityInfo {
  slug: string;
  value: string;
  id: string; // alias kept for backward compat — same as value
  label: string;
}

/** Shown when a chosen address is outside every served city (by-point 404). */
export const NOT_SERVICEABLE_MESSAGE =
  'Rất tiếc, AhaFood chưa phục vụ khu vực bạn chọn. Vui lòng chọn địa chỉ khác.';

/**
 * Shown when detection itself failed (network/parse) and there was no previously
 * resolved city to fall back on. Distinct from {@link NOT_SERVICEABLE_MESSAGE}:
 * the address may well be serviceable — we just couldn't ask.
 */
export const LOCATION_UNRESOLVED_MESSAGE =
  'Không xác định được khu vực của địa chỉ này. Vui lòng chọn lại địa chỉ.';

/**
 * Forced-fallback city used ONLY on a genuine detection error (network/parse) —
 * NOT for "address outside serviced area", which returns null so the caller can
 * warn the user. Đà Nẵng is the designated default serviced city.
 */
export const DEFAULT_CITY: CityInfo = {
  slug: 'da-nang',
  value: 'DAD',
  id: 'DAD',
  label: 'Đà Nẵng',
};

/**
 * Turn a routing slug into a human-ish label when the authoritative
 * `name_vi_vn` is not available (e.g. a bare deep-link before by-point runs).
 * "da-nang" → "Da Nang". Diacritics can't be recovered from a slug, so this is
 * a best-effort fallback only.
 */
export function prettifyCitySlug(slug?: string | null): string {
  if (!slug) return '';
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * Extract lat/lng from URL search params
 * @param searchParams - URL search parameters
 * @returns Object with lat, lng, and hasLocation flag
 */
export function extractLocationFromUrlParams(searchParams: {
  lat?: string | string[];
  lng?: string | string[];
}): {
  lat?: number;
  lng?: number;
  hasLocation: boolean;
} {
  const latParam = Array.isArray(searchParams.lat)
    ? searchParams.lat[0]
    : searchParams.lat;
  const lngParam = Array.isArray(searchParams.lng)
    ? searchParams.lng[0]
    : searchParams.lng;

  if (latParam && lngParam) {
    const lat = parseFloat(latParam);
    const lng = parseFloat(lngParam);

    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, hasLocation: true };
    }
  }

  return { hasLocation: false };
}

/**
 * Create location params object for API calls
 * @param lat Latitude
 * @param lng Longitude
 * @param defaultRadius Default radius if not provided
 * @returns Object with lat, lng, and radius for API calls
 */
export function createLocationParams(
  lat?: number,
  lng?: number,
  defaultRadius = 10000
): { lat?: number; lng?: number; radius?: number } {
  if (lat && lng) {
    return { lat, lng, radius: defaultRadius };
  }
  return {};
}

/**
 * Generate place_id from coordinates (for backward compatibility)
 * @param lat Latitude
 * @param lng Longitude
 * @returns Place ID string
 */
export function generatePlaceId(lat: number, lng: number): string {
  return `${lat}_${lng}`;
}

/**
 * Build a {@link CityInfo} directly from a `cities/by-point` response. Returns
 * null when the response lacks a slug/id (caller falls back to DEFAULT_CITY).
 */
function toCityInfo(detail: {
  id: string;
  slug?: string;
  service_city_id?: string;
  name?: string;
  name_vi_vn?: string;
}): CityInfo | null {
  const value = detail.service_city_id ?? detail.id;
  if (!detail.slug || !value) return null;
  return {
    slug: detail.slug,
    value,
    id: value,
    label: detail.name_vi_vn ?? detail.name ?? prettifyCitySlug(detail.slug),
  };
}

/**
 * Resolve the city of a coordinate via Ahamove's `cities/by-point` API.
 *
 * Returns:
 *  - a {@link CityInfo} when the coordinate is in a served city;
 *  - `null` when the coordinate is OUTSIDE every served city
 *    (`not_serviceable` / 404) — caller must warn the user, NOT navigate;
 *  - {@link DEFAULT_CITY} (Đà Nẵng) only on a genuine network/parse error,
 *    where a forced fallback is acceptable.
 */
export async function resolveCityFromCoordinates(
  lat: number,
  lng: number
): Promise<CityInfo | null> {
  try {
    const outcome = await getCityByPointFn(lat, lng);
    if (outcome.status === 'not_serviceable') return null;
    if (outcome.status === 'ok') {
      return toCityInfo(outcome.city) ?? DEFAULT_CITY;
    }
  } catch (error) {
    console.warn('resolveCityFromCoordinates failed:', error);
  }

  // Genuine error → forced fallback.
  return DEFAULT_CITY;
}

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param point1 First coordinate {lat, lng}
 * @param point2 Second coordinate {lat, lng}
 * @returns Distance in meters
 */
export function calculateDistance(
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number {
  const R = 6371000; // Earth radius in meters
  const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
  const dLng = ((point2.lng - point1.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((point1.lat * Math.PI) / 180) *
      Math.cos((point2.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Create address object from coordinates
 * @param lat Latitude
 * @param lng Longitude
 * @param address Optional address string
 * @returns Basic address object
 */
export function createBasicAddress(lat: number, lng: number, address?: string) {
  return {
    lat,
    lng,
    address: address ?? `${lat}, ${lng}`,
    place_id: generatePlaceId(lat, lng),
    second_label: address ?? 'Vị trí của bạn',
  };
}
